import React from 'react';
import { Card, Row, Col, Statistic, Divider, Tooltip } from 'antd';
import { FireOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../utils/danishNumberFormatter';
import { COLORS } from '../../constants/theme';

// Danish labels for CAPEX items
const CAPEX_LABELS = {
  htes: 'HTES (Rock store)',
  tilslutning: 'Tilslutning',
  ltes: 'LTES tank',
  turbine: 'Turbine',
  ledningsnet: 'Ledningsnet',
  tilkoblinger: 'Tilkoblinger (1475 stk)',
  industrigrund: 'Industrigrund',
  veksler: 'Veksler til overskudsvarme',
};

// Danish labels for OPEX items
const OPEX_LABELS = {
  elkøbOperational: 'Elkøb (Spot + TSO)',
  solOperational: 'Sol (Spotpris)',
  overskudsvarme: 'Overskudsvarme',
  forsyningDkLicens: 'Forsyning DK licens',
  it: 'IT',
  revision: 'Revision',
  administration: 'Administration (50 DKK/MWh)',
  driftVedligehold: 'Drift & Vedligehold (175 DKK/MWh)',
  diverse: 'Diverse',
};

// Danish labels for Revenue items
const REVENUE_LABELS = {
  varmesalg: 'Varmesalg',
  elsalgTurbine: 'Elsalg (Turbine)',
  systemydelserTurbine: 'Systemydelser (Turbine)',
  kapacitetsbetaling: 'Kapacitetsbetaling',
};

// Danish labels for Depreciation items with years
const DEPRECIATION_LABELS = {
  htes: 'HTES / 25 år',
  turbine: 'Turbine / 15 år',
  ltes: 'LTES / 25 år',
  ledningsnet: 'Ledningsnet / 50 år',
  bygninger: 'Bygninger / 50 år',
  orc: 'ORC / 15 år',
  tilkoblinger: 'Tilkoblinger / 50 år',
  industrigrund: 'Industrigrund / 50 år',
  veksler: 'Veksler / 25 år',
  software: 'Software / 7 år',
};

/**
 * Heat Company Financial Dashboard
 * Shows financial metrics specific to heat company operations
 */
const HeatCompanyFinancials = ({ financialData }) => {
  if (!financialData || !financialData.heat) {
    return null;
  }

  const { heat } = financialData;

  return (
    <div>
      <h3 style={{ 
        color: COLORS.operational.heater, 
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <FireOutlined /> Varmeselskab
      </h3>

      <Row gutter={[16, 16]}>
        {/* Revenue Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                Indtægter
                <Tooltip title="Varmeselskabets indtægter kommer primært fra varmesalg til kunder, plus eventuel elsalg fra turbine og systemydelser fra balancemarkedet.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(heat.revenue).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {REVENUE_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total Indtægt"
              value={heat.totalRevenue}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.financial.revenue, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>

        {/* OPEX Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                OPEX (Driftsomkostninger)
                <Tooltip title="Driftsomkostninger inkluderer både variable energikøb (el, sol, overskudsvarme) og faste omkostninger (licens, IT, revision, administration, drift & vedligehold). Disse omkostninger skal dækkes årligt af varmeprisen.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(heat.opex).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {OPEX_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total OPEX"
              value={heat.totalOpex}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.financial.opex, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>

        {/* Depreciation Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                Afskrivninger
                <Tooltip title="Årlige afskrivninger af varmeselskabets anlæg baseret på deres forventede levetid. HTES og LTES afskrives over 25 år, ledningsnet og tilslutning over 50 år, turbine over 15 år, osv. Samlet afskrivning er i millioner DKK årligt.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(heat.depreciation).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {DEPRECIATION_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value * 1000000, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total Afskrivning"
              value={heat.totalDepreciation * 1000000}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.status.error, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
        {/* CAPEX Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                CAPEX (Investeringer)
                <Tooltip title="Varmeselskabets samlede investeringer i infrastruktur inkluderer HTES rock store (40M), Ledningsnet (291M), LTES tank, Turbine, Tilslutning, samt alle fælles omkostninger som Bygninger, ORC, Projektledelse, Software og Diverse. Alt er 100% allokeret til varmeselskabet som non-profit forsyningsselskab.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(heat.capex).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {CAPEX_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value * 1000000, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total CAPEX"
              value={heat.totalCapex * 1000000}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.status.error, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>
      </Row>

      {/* P&L Summary */}
      <Card
        title={
          <span>
            Resultatopgørelse (P&L)
            <Tooltip title="Varmeselskabets resultatopgørelse viser indtægter fra varmesalg og elsalg fra turbine, minus driftsomkostninger (energikøb, licens, administration mv.) og afskrivninger. Som non-profit selskab skal alle omkostninger dækkes af varmeprisen.">
              <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
            </Tooltip>
          </span>
        }
        style={{
          marginTop: '1rem',
          borderColor: heat.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
        }}
        size="small"
      >
        <Row gutter={16}>
          <Col span={4}>
            <Tooltip title={`Varmeselskabets samlede indtægter fra varmesalg (${(heat.revenue.varmesalg / 1000000).toFixed(1)}M DKK) plus eventuel elsalg fra turbine (${(heat.revenue.elsalgTurbine / 1000000).toFixed(1)}M DKK) og systemydelser fra turbine (${(heat.revenue.systemydelserTurbine / 1000000).toFixed(1)}M DKK).`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Indtægt <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={heat.totalRevenue}
                  precision={0}
                  suffix="DKK"
                  prefix="+"
                  valueStyle={{ color: COLORS.status.success, fontWeight: 'bold' }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Varmeselskabets driftsomkostninger inkluderer: Elkøb operational (${(heat.opex.elkøbOperational / 1000000).toFixed(1)}M), Sol (${(heat.opex.solOperational / 1000000).toFixed(1)}M), Overskudsvarme fra fabrik (${(heat.opex.overskudsvarme / 1000000).toFixed(1)}M), plus faste omkostninger som Forsyning DK licens (${(heat.opex.forsyningDkLicens / 1000000).toFixed(2)}M), IT, Revision, Administration, Drift & Vedligehold.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>OPEX <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={heat.totalOpex}
                  precision={0}
                  suffix="DKK"
                  prefix="−"
                  valueStyle={{ color: COLORS.status.error, fontWeight: 'bold' }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`EBITDA for varmeselskabet er driftsresultatet FØR afskrivninger. Beregnes som: Indtægt (${(heat.totalRevenue / 1000000).toFixed(1)}M) minus OPEX (${(heat.totalOpex / 1000000).toFixed(1)}M) = ${(heat.ebitda / 1000000).toFixed(1)}M DKK. Viser den rene driftsøkonomi for varmeproduktion.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>EBITDA <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={heat.ebitda}
                  precision={0}
                  suffix="DKK"
                  prefix={heat.ebitda >= 0 ? "+" : "−"}
                  valueStyle={{
                    color: heat.ebitda >= 0 ? COLORS.status.success : COLORS.status.error,
                    fontWeight: 'bold',
                  }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Varmeselskabets årlige afskrivninger fordelt på anlæg: HTES rock store (${(heat.depreciation.htes || 0).toFixed(2)}M, 25 år), Ledningsnet (${(heat.depreciation.ledningsnet || 0).toFixed(2)}M, 50 år), Turbine (${(heat.depreciation.turbine || 0).toFixed(2)}M, 15 år), LTES tank (${(heat.depreciation.ltes || 0).toFixed(2)}M, 25 år), Tilslutning (${(heat.depreciation.tilslutning || 0).toFixed(2)}M, 50 år), Bygninger, ORC, osv.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Afskrivning <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={heat.totalDepreciation * 1000000}
                  precision={0}
                  suffix="DKK"
                  prefix="−"
                  valueStyle={{ color: COLORS.status.error, fontWeight: 'bold' }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Varmeselskabets nettoresultat efter alle omkostninger. Beregnes som: EBITDA (${(heat.ebitda / 1000000).toFixed(1)}M) minus Afskrivninger (${(heat.totalDepreciation).toFixed(2)}M) = ${(heat.netProfit / 1000000).toFixed(1)}M DKK. Som non-profit selskab skal dette overskud geninvesteres eller føre til lavere varmepriser fremadrettet.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Nettoresultat <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={Math.abs(heat.netProfit)}
                  precision={0}
                  suffix="DKK"
                  prefix={heat.netProfit >= 0 ? "+" : "−"}
                  valueStyle={{
                    color: heat.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
                    fontWeight: 'bold',
                    fontSize: '1.125rem',
                  }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Nettomargin viser hvor stor en andel af omsætningen der bliver til overskud. Beregnes som: Nettoresultat (${(heat.netProfit / 1000000).toFixed(1)}M) / Indtægt (${(heat.totalRevenue / 1000000).toFixed(1)}M) × 100 = ${(heat.totalRevenue > 0 ? (heat.netProfit / heat.totalRevenue * 100) : 0).toFixed(1)}%.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Nettomargin <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={heat.totalRevenue > 0 ? (heat.netProfit / heat.totalRevenue * 100) : 0}
                  precision={1}
                  suffix="%"
                  valueStyle={{
                    color: heat.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
                  }}
                />
              </div>
            </Tooltip>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default HeatCompanyFinancials;
